import { FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Input } from '../atoms';
import { useToast } from '../organisms';

/**
 * LoginPage — CoreOS auth surface at /login.
 */

export function LoginPage() {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const next = params.get('next') || '/';

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || 'Invalid password');
        toast({ variant: 'error', message: body.error || 'Invalid password' });
        return;
      }
      toast({ variant: 'success', message: 'Signed in.' });
      await queryClient.invalidateQueries({ queryKey: ['current-user'] });
      navigate(next, { replace: true });
    } catch (err) {
      const msg = (err as Error).message || 'Network error';
      setError(msg);
      toast({ variant: 'error', message: msg });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      background: 'var(--color-bg)',
      padding: 'var(--sp-5)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 380,
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-2xl)',
        boxShadow: 'var(--sh-lg)',
        padding: 'var(--sp-7)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 'var(--sp-6)' }}>
          <div style={{
            width: 56, height: 56,
            margin: '0 auto var(--sp-3)',
            borderRadius: 'var(--r-xl)',
            background: 'linear-gradient(160deg, var(--brand-primary) 0%, var(--brand-secondary) 100%)',
            display: 'grid', placeItems: 'center',
            color: '#fff',
            fontWeight: 'var(--fw-bold)',
            fontSize: 'var(--fs-xl)',
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
          }}>CO</div>
          <h1 style={{
            margin: 0,
            fontSize: 'var(--fs-2xl)',
            fontWeight: 'var(--fw-semibold)',
            color: 'var(--color-ink)',
            letterSpacing: 'var(--ls-tight)',
          }}>CoreOS</h1>
          <p style={{
            margin: 'var(--sp-1) 0 0',
            color: 'var(--color-ink-3)',
            fontSize: 'var(--fs-sm)',
          }}>Sign in to continue</p>
        </div>

        <form onSubmit={handleSubmit}>
          <label style={{
            display: 'block',
            marginBottom: 'var(--sp-4)',
          }}>
            <span style={{
              display: 'block',
              marginBottom: 'var(--sp-1)',
              fontSize: 'var(--fs-sm)',
              fontWeight: 'var(--fw-medium)',
              color: 'var(--color-ink)',
            }}>Password</span>
            <Input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoFocus
              autoComplete="current-password"
              invalid={!!error}
              required
            />
            {error && (
              <div style={{
                marginTop: 'var(--sp-1)',
                fontSize: 'var(--fs-sm)',
                color: 'var(--color-status-red)',
              }}>{error}</div>
            )}
          </label>

          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={submitting}
            disabled={!password || submitting}
            style={{ width: '100%' }}
          >
            Sign in
          </Button>
        </form>

        <footer style={{
          marginTop: 'var(--sp-5)',
          paddingTop: 'var(--sp-4)',
          borderTop: '1px solid var(--color-border)',
          textAlign: 'center',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-xs)',
        }}>
          CoreOS Platform
        </footer>
      </div>
    </div>
  );
}
